'use client'

// React Imports
import type { ReactNode } from 'react'
import { createContext, useEffect, useMemo, useRef } from 'react'

// Third-party Imports
import { useTheme } from 'next-themes'

// Hook Imports
import { useObjectCookie } from '@/hooks/use-object-cookie'

// Config Imports
import themeConfig from '@/configs/themeConfig'
import {
  RADIUS_VALUES,
  initialSettings,
  sanitizeSettings,
  type Collapsible,
  type FontKey,
  type Layout,
  type Mode,
  type Radius,
  type Scale,
  type Settings,
  type ThemePresetKey,
  type Variant
} from '@/configs/theme-settings'

// Util Imports
import { FONT_CONFIG } from '@/utils/fonts'
import { themePresets } from '@/utils/theme-presets'

/*
 * THE PERSISTENCE RULE FOR THIS TEMPLATE.
 *
 * ! THEME IS THE ONLY THING THAT SURVIVES A RELOAD. Everything else - record stores, pipeline
 * ! columns, workspace identity, notification preferences - re-seeds on every full page load.
 *
 * ! IT IS A COOKIE, NOT `localStorage`, and the two are not interchangeable. `src/app/layout.tsx`
 * ! reads this cookie ON THE SERVER and renders the first paint already in the right mode and
 * ! preset; `localStorage` cannot be read until after mount, so the same feature built on it
 * ! flashes the default colours before correcting itself.
 *
 * ! Two other cookies exist and are infrastructure, not precedent: `lib/view-preference.ts` (which
 * ! view a module opens in, server-read for the same reason) and `ui/sidebar.tsx` (stock shadcn).
 */

export { RADIUS_VALUES, initialSettings }
export type { Collapsible, FontKey, Layout, Mode, Radius, Scale, Settings, ThemePresetKey, Variant }

const PRESET_CSS_VARS = [
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'border',
  'input',
  'ring',
  'chart-1',
  'chart-2',
  'chart-3',
  'chart-4',
  'chart-5',
  'sidebar',
  'sidebar-foreground',
  'sidebar-primary',
  'sidebar-primary-foreground',
  'sidebar-accent',
  'sidebar-accent-foreground',
  'sidebar-border',
  'sidebar-ring',
  'shadow-color',
  'shadow-opacity',
  'shadow-blur',
  'shadow-spread',
  'shadow-offset-x',
  'shadow-offset-y'
] as const

type BroadcastMessage = {
  type: 'SETTINGS_UPDATED'
  payload: Settings
}

type SettingsContextProps = {
  settings: Settings
  updateSettings: (settings: Partial<Settings>) => void
}

type Props = {
  children: ReactNode
  settingsCookie?: Settings
}

const isTypingTarget = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

export const SettingsContext = createContext<SettingsContextProps | null>(null)

export const SettingsProvider = (props: Props) => {
  // Props
  const { children } = props

  // Hooks
  const { setTheme, resolvedTheme } = useTheme()

  const broadcastChannelRef = useRef<BroadcastChannel | null>(null)

  const [cookie, updateCookie] = useObjectCookie<Settings>(themeConfig.settingsCookieName, {
    ...initialSettings,
    ...props.settingsCookie
  })

  const settings: Settings = useMemo(() => ({ ...initialSettings, ...sanitizeSettings(cookie) }), [cookie])

  useEffect(() => {
    if (!props.settingsCookie) {
      updateCookie(initialSettings)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (typeof window !== 'undefined' && 'BroadcastChannel' in window) {
      try {
        if (!broadcastChannelRef.current) {
          broadcastChannelRef.current = new BroadcastChannel(themeConfig.settingsCookieName)
        }

        const handleMessage = (event: MessageEvent<BroadcastMessage>) => {
          const { type, payload } = event.data

          if (type === 'SETTINGS_UPDATED') {
            updateCookie(payload)
          }
        }

        broadcastChannelRef.current.onmessage = handleMessage

        return () => {
          broadcastChannelRef.current?.close()
          broadcastChannelRef.current = null
        }
      } catch (error) {
        console.error('BroadcastChannel error:', error)

        if (broadcastChannelRef.current) {
          try {
            broadcastChannelRef.current.close()
          } catch {
            // Ignore close errors
          }

          broadcastChannelRef.current = null
        }
      }
    }
  }, [updateCookie])

  const broadcastSettingsUpdate = (updatedSettings: Settings) => {
    if (typeof window === 'undefined' || !('BroadcastChannel' in window)) {
      return
    }

    if (!broadcastChannelRef.current) {
      try {
        broadcastChannelRef.current = new BroadcastChannel(themeConfig.settingsCookieName)
      } catch (error) {
        console.error('Failed to create BroadcastChannel', error)

        return
      }
    }

    try {
      broadcastChannelRef.current.postMessage({
        type: 'SETTINGS_UPDATED',
        payload: updatedSettings
      })
    } catch (error) {
      console.error('Failed to broadcast settings update', error)

      try {
        broadcastChannelRef.current.close()
      } catch {
        // Ignore close errors
      }

      broadcastChannelRef.current = null
    }
  }

  const updateSettings = (newSettings: Partial<Settings>) => {
    const updatedSettings = { ...settings, ...newSettings }

    updateCookie(updatedSettings)

    if (newSettings.mode) {
      setTheme(newSettings.mode)
    }

    broadcastSettingsUpdate(updatedSettings)
  }

  useEffect(() => {
    setTheme(settings.mode)
  }, [settings.mode, setTheme])

  const updateSettingsRef = useRef(updateSettings)
  const resolvedThemeRef = useRef(resolvedTheme)

  useEffect(() => {
    updateSettingsRef.current = updateSettings
    resolvedThemeRef.current = resolvedTheme
  })

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.repeat || event.metaKey || event.ctrlKey || event.altKey) {
        return
      }

      if (event.key?.toLowerCase() !== 'd' || isTypingTarget(event.target)) {
        return
      }

      updateSettingsRef.current({ mode: resolvedThemeRef.current === 'dark' ? 'light' : 'dark' })
    }

    window.addEventListener('keydown', onKeyDown)

    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  useEffect(() => {
    const root = document.documentElement

    if (settings.themePreset === 'default') {
      PRESET_CSS_VARS.forEach(key => root.style.removeProperty(`--${key}`))

      return
    }

    const preset = themePresets[settings.themePreset]

    if (!preset) return

    const mode = resolvedTheme === 'dark' ? 'dark' : 'light'

    Object.entries(preset.styles[mode]).forEach(([key, value]) => {
      if (value !== undefined) root.style.setProperty(`--${key}`, value as string)
    })
  }, [settings.themePreset, resolvedTheme])

  useEffect(() => {
    document.documentElement.style.setProperty('--radius', RADIUS_VALUES[settings.radius])
  }, [settings.radius])

  useEffect(() => {
    if (settings.scale === 'md') {
      document.documentElement.removeAttribute('data-theme-scale')
    } else {
      document.documentElement.setAttribute('data-theme-scale', settings.scale)
    }
  }, [settings.scale])

  useEffect(() => {
    const root = document.documentElement
    const fontVar = FONT_CONFIG[settings.font]?.variable ?? '--font-manrope'

    root.style.setProperty('--font-sans', `var(${fontVar})`)
    root.style.setProperty('font-family', `var(${fontVar})`)
  }, [settings.font])

  return (
    <SettingsContext.Provider
      value={{
        settings,
        updateSettings
      }}
    >
      {children}
    </SettingsContext.Provider>
  )
}
